import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { InputContainer } from '../../Layout/InputContainer';
import { InputArea } from '../../Layout/Items/inputArea';
import { DefaultDisplay } from '../../Partials/DefaultDisplay';
import { Header } from '../../Header/Header';
import MessagesList from '../../Layout/Views/MessagesList';
import StudyPanel from '../../Layout/Panels/StudyPanel';
import { useResourceContext } from '../../../contexts/ResourceContext';
import { useEditHandler } from '../../../hooks/useEditHandler';
import { useChatContext } from '../../../contexts/AppDataContext';
import { useMode } from '../../../contexts/ModeContext';
import { useInputContext } from '../../../contexts/InputContext';
import useScrollHandler from '../../../hooks/useScrollHandler';
import { useWindow } from '../../../contexts/WindowContext';
import { Mode } from '../../../types/mode';
import type { SubmitEvent } from '../hooks/useMessageSubmission';
import { useAskUser } from '../../../contexts/AskUserContext';
import { AskUserCard } from '../../Message/AskUserCard';

interface ChatContainerProps {
  handleSubmit: (e: any) => void;
  handleStopStreaming: () => void;
  regenerateMessage: (messageId: string, mode: Mode) => Promise<void>;
  triggerSubmit: (e?: SubmitEvent | React.FormEvent) => void;
  abortControllerRef: React.RefObject<AbortController | null>;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  handleSubmit,
  handleStopStreaming,
  regenerateMessage,
  triggerSubmit,
  abortControllerRef,
  messagesContainerRef
}) => {
  const { windowWidth } = useWindow();
  const { mode } = useMode();
  const {
    filteredMessages,
    currentChatId,
    isGeneratingResponse,
    isSubmitting
  } = useChatContext();

  const { isResourcesVisible, handleResourceClick } = useResourceContext();
  const resourcePanelRef = useRef<ImperativePanelHandle>(null);
  const lastResourcePanelSizeRef = useRef(50);

  useEffect(() => {
    if (windowWidth < 768) return;
    const panel = resourcePanelRef.current;
    if (!panel) return;

    const frame = window.requestAnimationFrame(() => {
      if (isResourcesVisible) {
        panel.resize(lastResourcePanelSizeRef.current);
      } else {
        const currentSize = panel.getSize();
        if (currentSize > 0) lastResourcePanelSizeRef.current = currentSize;
        panel.collapse();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isResourcesVisible, windowWidth]);

  const {
    input,
    setInput,
    textareaProps,
    internalRef,
  } = useInputContext();

  const { ScrollToBottomButton, Spacer } = useScrollHandler({
    messagesContainerRef,
    isGeneratingResponse,
    chatId: currentChatId || '',
    messages: filteredMessages
  });

  const {
    handleEditSubmit,
    EditArea,
    editContent,
    setEditContent,
    editMode,
    setEditMode,
    editCurrentModel,
    setEditCurrentModel,
    prepareEditContent,
    updateHandleSubmit,
    editTextFiles,
    editImageFiles,
    addEditFiles,
    removeEditAttachment,
  } = useEditHandler();

  // Connect the handleSubmit function to the edit handler
  useEffect(() => {
    updateHandleSubmit(handleSubmit);
  }, [handleSubmit, updateHandleSubmit]);

  const memoizedEditActions = useMemo(() => ({
    handleEditSubmit,
    setEditContent
  }), [handleEditSubmit, setEditContent]);

  const memoizedMessageActions = useMemo(() => ({
    RegenerateMessage: regenerateMessage
  }), [regenerateMessage]);

  const getRenderFunctions = useCallback(() => ({
    EditArea,
    editContent,
    setEditContent,
    editMode,
    setEditMode,
    editCurrentModel,
    setEditCurrentModel,
    prepareEditContent,
    editTextFiles,
    editImageFiles,
    addEditFiles,
    removeEditAttachment,
  }), [EditArea, editContent, editMode, editCurrentModel, setEditContent, setEditMode, setEditCurrentModel, prepareEditContent, editTextFiles, editImageFiles, addEditFiles, removeEditAttachment]);

  const renderDefaultDisplay = useCallback(() => {
    const inputAreaProps = {
      input,
      isSubmitting,
      handleSubmit,
      textareaProps,
      internalRef,
      isChat: false,
      HandleStopStreaming: handleStopStreaming,
      setInput
    };

    return (
      <DefaultDisplay 
        InputArea={<InputArea {...inputAreaProps} />}
        isSubmitting={isSubmitting}
      />
    );
  }, [input, isSubmitting, handleSubmit, textareaProps, internalRef, handleStopStreaming, setInput]);

  const askUser = useAskUser();
  const activeQuestion = askUser?.isComposerOpen ? askUser.activeQuestion : null;

  const renderInputArea = useCallback(() => (
    activeQuestion && askUser ? (
      // An open question takes the input's place until it is answered or skipped.
      <AskUserCard
        key={`${activeQuestion.card.id}-${activeQuestion.card.status}`}
        card={activeQuestion.card}
        canAnswer={!isSubmitting}
        onSubmit={answers => askUser.answerQuestion(activeQuestion.messageId, activeQuestion.card.id, answers)}
        onSkip={askUser.dismiss}
      />
    ) : (
      <InputArea
        input={input}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        textareaProps={textareaProps}
        internalRef={internalRef}
        isChat={true}
        HandleStopStreaming={handleStopStreaming}
        setInput={setInput}
      />
    )
  ), [activeQuestion, askUser, input, isSubmitting, handleSubmit, textareaProps, internalRef, handleStopStreaming, setInput]);

  const renderMessagesList = useCallback(() => (
    <MessagesList
      renderDefaultDisplay={renderDefaultDisplay}
      chatId={currentChatId}
      handleResourceClick={handleResourceClick}
      memoizedEditActions={memoizedEditActions}
      memoizedMessageActions={memoizedMessageActions}
      mode={mode}
      getRenderFunctions={getRenderFunctions}
      messagesContainerRef={messagesContainerRef as React.RefObject<HTMLDivElement>}
      filteredMessages={filteredMessages}
      Spacer={Spacer as () => React.ReactElement}
    />
  ), [
    renderDefaultDisplay,
    currentChatId,
    handleResourceClick,
    memoizedEditActions,
    memoizedMessageActions,
    mode,
    getRenderFunctions,
    messagesContainerRef,
    filteredMessages,
    Spacer
  ]);

  const renderInputContainer = useCallback(() => (
    <InputContainer
      filteredMessages={filteredMessages}
      isSubmitting={isSubmitting}
      ScrollToBottomButton={ScrollToBottomButton}
      InputArea={renderInputArea()}
    />
  ), [filteredMessages, isSubmitting, ScrollToBottomButton, renderInputArea]);

  const renderStudyPanel = useCallback(() => (
    <StudyPanel
      setInput={setInput}
      triggerSubmit={triggerSubmit}
    />
  ), [setInput, triggerSubmit]);

  if (windowWidth >= 768) {
    return (
      <PanelGroup direction="horizontal" className="chat-area">
        <Panel className="chat-message-panel" defaultSize={isResourcesVisible ? 50 : 100} minSize={30}>
          <div className="study-view-container">
            <Header />
            {renderMessagesList()}
            {renderInputContainer()}
          </div>
        </Panel>
        <PanelResizeHandle
          className={`resize-handle${isResourcesVisible ? '' : ' is-hidden'}`}
          disabled={!isResourcesVisible}
        />
        <Panel
          ref={resourcePanelRef}
          className="study-resource-panel"
          defaultSize={isResourcesVisible ? 50 : 0}
          minSize={30}
          collapsedSize={0}
          collapsible
          onResize={(size) => {
            if (isResourcesVisible && size >= 30) {
              lastResourcePanelSizeRef.current = size;
            }
          }}
        >
          <div className="study-panel-container">
            {renderStudyPanel()}
          </div>
        </Panel>
      </PanelGroup>
    );
  }

  return (
    <div className="chat-area">
      <Header />
      <div className="study-view-container">
        {renderMessagesList()}
        {renderInputContainer()}
      </div>
      <div className="study-panel-container">
        {renderStudyPanel()}
      </div>
    </div>
  );
};
